import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';
import { formatMoney } from '@theme/money-formatting';
import { SlideshowSelectEvent } from '@theme/events';

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Product page behaviour for sections/wc-product.liquid.
 *
 * - Variants: Horizon's <variant-picker> re-renders the page for the chosen variant and dispatches the
 *   standard ProductSelectEvent. We morph our [data-wc-region] blocks (price, buy button, sticky bar)
 *   from that HTML and move the slider to the variant's image.
 * - Quantity stepper keeps the "Add to bag · price" label in sync (price × quantity).
 * - "Product / In the room" toggle drives Horizon's <slideshow-component> and follows swipes.
 * - Accessible tabs (arrow keys, Home/End) with a sliding underline.
 * - Mobile sticky Add to bag bar shown while the main button is out of view.
 */
class WcProduct extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('input', this.#handleQtyInput, { signal });
    this.addEventListener('change', this.#handleQtyInput, { signal });
    this.addEventListener('keydown', this.#handleTabKeys, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });

    const section = this.closest('.shopify-section') ?? this;
    section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#updateQtyButtons();
    this.#moveTabInk(false);
    window.addEventListener('resize', () => this.#moveTabInk(false), { signal, passive: true });
    document.fonts?.ready.then(() => this.#moveTabInk(false));
    this.#initSticky(signal);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#stickyObserver?.disconnect();
    document.body.removeAttribute('data-wc-sticky-cta');
  }

  /* ------------------------------------------------------------ helpers */

  get qtyInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-wc-qty-input]'));
  }

  get quantity() {
    const value = Number(this.qtyInput?.value);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? '');
  }

  /* ------------------------------------------------------------ clicks */

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const qtyButton = target.closest('[data-wc-qty]');
    if (qtyButton instanceof HTMLElement) {
      this.#stepQty(Number(qtyButton.dataset.wcQty));
      return;
    }

    const viewButton = target.closest('[data-wc-view-btn]');
    if (viewButton instanceof HTMLElement) {
      this.#showView(viewButton.dataset.wcViewBtn ?? 'product');
      return;
    }

    const tab = target.closest('[role="tab"]');
    if (tab instanceof HTMLElement) this.#selectTab(tab);
  };

  /* ------------------------------------------------------------ quantity */

  /** @param {number} direction */
  #stepQty(direction) {
    const input = this.qtyInput;
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = Number(input.max) || 99;
    const next = Math.min(max, Math.max(min, this.quantity + direction * step));
    input.value = String(next);
    this.#afterQtyChange();
  }

  #handleQtyInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-wc-qty-input]')) return;
    if (event.type === 'change') {
      const input = event.target;
      const min = Number(input.min) || 1;
      const max = Number(input.max) || 99;
      input.value = String(Math.min(max, Math.max(min, Math.round(this.quantity))));
    }
    this.#afterQtyChange();
  };

  #afterQtyChange() {
    this.#updateQtyButtons();
    this.#updateLinePrice();
  }

  #updateQtyButtons() {
    const input = this.qtyInput;
    if (!input) return;
    const [minus, plus] = this.querySelectorAll('[data-wc-qty]');
    if (minus instanceof HTMLButtonElement) minus.disabled = this.quantity <= (Number(input.min) || 1);
    if (plus instanceof HTMLButtonElement) plus.disabled = this.quantity >= (Number(input.max) || 99);
  }

  #updateLinePrice() {
    const button = this.querySelector('[data-wc-add]');
    const unit = Number(button instanceof HTMLElement ? button.dataset.price : NaN);
    if (!Number.isFinite(unit)) return;
    const text = this.#money(unit * this.quantity);
    for (const node of this.querySelectorAll('[data-wc-line-price]')) node.textContent = text;
  }

  /* ------------------------------------------------------------ variants */

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        const fresh =
          html.querySelector(`wc-product[data-section-id="${this.dataset.sectionId}"]`) ??
          html.querySelector('wc-product');
        if (!fresh) return;

        for (const region of this.querySelectorAll('[data-wc-region]')) {
          const key = /** @type {HTMLElement} */ (region).dataset.wcRegion;
          const next = fresh.querySelector(`[data-wc-region="${key}"]`);
          if (next) morph(region, next);
        }
        this.#updateLinePrice();

        // Jump to the variant's image when it has one.
        const mediaId = fresh.getAttribute('data-variant-media-id');
        if (mediaId) this.#selectSlideByMedia(mediaId);
      })
      .catch(() => {});
  };

  /** @param {string} mediaId */
  #selectSlideByMedia(mediaId) {
    const slideshow = this.#slideshow;
    if (!slideshow) return;
    const slides = [...this.querySelectorAll('.wc-pdp__stage slideshow-slide')];
    const index = slides.findIndex((slide) => slide.getAttribute('data-media-id') === String(mediaId));
    if (index >= 0 && typeof slideshow.select === 'function') slideshow.select(index);
  }

  /* ------------------------------------------------------------ media views */

  get #slideshow() {
    return /** @type {any} */ (this.querySelector('.wc-pdp__stage slideshow-component'));
  }

  get #roomIndex() {
    const stage = this.querySelector('.wc-pdp__stage');
    return Number(stage instanceof HTMLElement ? stage.dataset.roomIndex : -1);
  }

  /** @param {string} view */
  #showView(view) {
    const slideshow = this.#slideshow;
    const roomIndex = this.#roomIndex;
    if (!slideshow || typeof slideshow.select !== 'function') return;
    if (view === 'room' && roomIndex >= 0) slideshow.select(roomIndex);
    else if (view === 'product') slideshow.select(0);
    this.#syncViewButtons(view);
  }

  /** @param {string} view */
  #syncViewButtons(view) {
    for (const button of this.querySelectorAll('[data-wc-view-btn]')) {
      button.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (button).dataset.wcViewBtn === view));
    }
  }

  #handleSlideSelect = (event) => {
    const index = /** @type {any} */ (event).detail?.index;
    if (typeof index !== 'number') return;
    this.#syncViewButtons(index === this.#roomIndex ? 'room' : 'product');
  };

  /* ------------------------------------------------------------ tabs */

  /** @param {HTMLElement} tab */
  #selectTab(tab, focus = false) {
    const list = tab.closest('[role="tablist"]');
    if (!list) return;
    for (const other of list.querySelectorAll('[role="tab"]')) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.setAttribute('tabindex', selected ? '0' : '-1');
      const panel = this.querySelector(`#${other.getAttribute('aria-controls')}`);
      if (panel instanceof HTMLElement) panel.hidden = !selected;
    }
    if (focus) tab.focus();
    this.#moveTabInk(true);
  }

  /** @param {KeyboardEvent} event */
  #handleTabKeys = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLElement)) return;
    const tabs = /** @type {HTMLElement[]} */ ([...(tab.parentElement?.querySelectorAll('[role="tab"]') ?? [])]);
    const index = tabs.indexOf(tab);
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = (index + 1) % tabs.length;
    else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    const target = tabs[next];
    if (target) this.#selectTab(target, true);
  };

  /** @param {boolean} animate */
  #moveTabInk(animate) {
    const ink = this.querySelector('.wc-pdp__tab-ink');
    const active = this.querySelector('[role="tab"][aria-selected="true"]');
    if (!(ink instanceof HTMLElement) || !(active instanceof HTMLElement)) return;
    ink.style.transition = animate && !reduceMotion() ? '' : 'none';
    ink.style.width = `${active.offsetWidth}px`;
    ink.style.transform = `translateX(${active.offsetLeft}px)`;
  }

  /* ------------------------------------------------------------ sticky bar */

  /** @type {IntersectionObserver | undefined} */
  #stickyObserver;

  /** @param {AbortSignal} signal */
  #initSticky(signal) {
    const bar = this.querySelector('[data-wc-sticky]');
    const anchor = this.querySelector('[data-wc-add]');
    if (!(bar instanceof HTMLElement) || !anchor) return;

    const mobile = window.matchMedia('(max-width: 989px)');
    let anchorVisible = true;

    const update = () => {
      const show = mobile.matches && !anchorVisible;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      for (const control of bar.querySelectorAll('button')) control.setAttribute('tabindex', show ? '0' : '-1');
      document.body.toggleAttribute('data-wc-sticky-cta', show);
    };

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        anchorVisible = Boolean(entry?.isIntersecting);
        update();
      },
      { rootMargin: '0px 0px -40px 0px' }
    );
    this.#stickyObserver.observe(anchor);
    mobile.addEventListener('change', update, { signal });
  }
}

if (!customElements.get('wc-product')) customElements.define('wc-product', WcProduct);
